import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import { CircularProgress, Box, Button, Typography } from "@mui/material";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  getWithTokenAPI,
  postWithoutTokenAPI,
  putAPI,
  deleteAPI,
  postWithTokenAPI,
} from "../../utils/Api";
import BuildIcon from "@mui/icons-material/Build";
import PermContactCalendarIcon from "@mui/icons-material/PermContactCalendar";

function AdminManagement() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("active");
  const [allAdmins, setAllAdmins] = useState([]);
  const [filteredAdmins, setFilteredAdmins] = useState([]);
  const [rolesdata, setrolesdata] = useState([]);
  const [showAddAdminModal, setShowAddAdminModal] = useState(false);
  const [newAdmin, setNewAdmin] = useState({
    name: "",
    email: "",
    password: "",
    phone_no: "",
    address: "",
    gender: "",
    date_of_birth: "",
    role_id: "",
  });
  const primaryGreen = "#DC3545";
  const lightGreen = "#ffe5e5";
  const permissions = JSON.stringify(localStorage.getItem("permissions"));
  const [addAdminErrorMsg, setAddAdminErrorMsg] = useState("");
  const [isAddingAdmin, setIsAddingAdmin] = useState(false); // Loading state for modal submit
  const [showPassword, setShowPassword] = useState(false); // New state for password visibility

  const fetchAdmins = async (statusFilter = null) => {
    setLoading(true);
    const token = localStorage.getItem("token");
    try {
      let url = "/admin/find-all";
      if (statusFilter) {
        url += `?status=${statusFilter}`;
      }
      const res = await getWithTokenAPI(token, url);
      if (res.statusCode === 200) {
        const mappedAdmins = (res.data.admin_list || []).map((admin) => ({
          ...admin,
          phone_no: admin.phone_number || "", // Use phone_number from backend, default to empty string
        }));
        setAllAdmins(mappedAdmins);
      } else {
        toast.error(res.message || "Failed to load admins.");
        setAllAdmins([]);
      }
    } catch (error) {
      console.error("Fetch admins error:", error);
      toast.error("Failed to load admins due to a network error.");
      setAllAdmins([]);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = async (tabId) => {
    setActiveTab(tabId);
    if (tabId === "all") {
      await fetchAdmins();
    } else if (tabId === "active") {
      await fetchAdmins("Active");
    } else if (tabId === "inactive") {
      await fetchAdmins("Inactive");
    }
  };

  useEffect(() => {
    handleTabChange("active"); // Initial fetch on component mount
  }, []);

  useEffect(() => {
    let filtered = [];
    if (activeTab === "active") {
      filtered = allAdmins.filter((admin) => admin.status === "Active");
    } else if (activeTab === "inactive") {
      filtered = allAdmins.filter((admin) => admin.status === "Inactive");
    } else {
      filtered = allAdmins; // Fallback for 'all' or other tabs
    }
    setFilteredAdmins(filtered);
  }, [allAdmins, activeTab]);

  const toggleAdminStatus = async (adminId, currentStatus) => {
    setLoading(true);
    const token = localStorage.getItem("token");
    const newStatus = currentStatus === "Active" ? "Inactive" : "Active";

    try {
      const res = await putAPI(token, `/admin/update`, {
        _id: adminId,
        status: newStatus,
      });

      if (res.statusCode === 200) {
        toast.success(res.message || "Admin status updated successfully!", {
          autoClose: 1000,
          onClose: () => handleTabChange(activeTab),
        });
      } else {
        toast.error(res.message || "Failed to update admin status.");
      }
    } catch (error) {
      console.error("Toggle status error:", error);
      toast.error("Failed to update status due to a network error.");
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (adminId) => {
    navigate(`/backoffice/admin-details/${adminId}`);
  };

  const handleAddAdminClick = () => {
    setShowAddAdminModal(true);
    setNewAdmin({
      name: "",
      email: "",
      password: "",
      phone_no: "",
      address: "",
      gender: "",
      date_of_birth: "",
      role_id: "",
    });
    setAddAdminErrorMsg(""); // Clear previous errors
    setShowPassword(false); // Reset password visibility when opening modal
  };

  const handleDeleteAdmin = async (adminId) => {
    if (
      window.confirm(
        "Are you sure you want to delete this admin? This action cannot be undone."
      )
    ) {
      setLoading(true);
      const token = localStorage.getItem("token");

      try {
        const res = await deleteAPI(token, `/admin/delete`, { _id: adminId });

        if (res.statusCode === 200) {
          toast.success(res.message || "Admin deleted successfully!", {
            autoClose: 1000,
            onClose: () => handleTabChange(activeTab),
          });
        } else {
          toast.error(res.message || "Failed to delete admin.");
        }
      } catch (error) {
        console.error("Delete admin error:", error);
        toast.error("Failed to delete admin due to a network error.");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleNewAdminInputChange = (e) => {
    const { name, value } = e.target;
    setNewAdmin((prev) => ({ ...prev, [name]: value }));
    setAddAdminErrorMsg("");
  };

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  // Handles submission of the Add Admin form
  const handleAddAdminSubmit = async (e) => {
    e.preventDefault();
    setAddAdminErrorMsg("");

    if (!newAdmin.name.trim()) {
      setAddAdminErrorMsg("Admin name is required.");
      return;
    }
    if (!newAdmin.email.trim()) {
      setAddAdminErrorMsg("Email is required.");
      return;
    }
    if (!/\S+@\S+\.\S+/.test(newAdmin.email.trim())) {
      setAddAdminErrorMsg("Invalid email format.");
      return;
    }
    if (!newAdmin.password) {
      setAddAdminErrorMsg("Password is required.");
      return;
    }
    if (newAdmin.password.length < 6) {
      setAddAdminErrorMsg("Password must be at least 6 characters.");
      return;
    }
    if (!newAdmin.phone_no.trim()) {
      setAddAdminErrorMsg("Phone number is required.");
      return;
    }
    if (!newAdmin.address.trim()) {
      setAddAdminErrorMsg("Address is required.");
      return;
    }
    if (!newAdmin.gender) {
      setAddAdminErrorMsg("Gender is required.");
      return;
    }
    if (!newAdmin.date_of_birth) {
      setAddAdminErrorMsg("Date of Birth is required.");
      return;
    }

    setIsAddingAdmin(true); // Set loading state for modal submit
    const token = localStorage.getItem("token");
    const payload = {
      name: newAdmin.name.trim(),
      email: newAdmin.email.trim(),
      password: newAdmin.password,
      phone_number: newAdmin.phone_no.trim(),
      address: newAdmin.address.trim(),
      gender: newAdmin.gender,
      dob: newAdmin.date_of_birth,
      role_id: newAdmin.role_id,
    };

    console.log("Payload being sent to /admin/create:", payload);

    try {
      const response = await postWithTokenAPI(token,`/admin/create`, payload);

      console.log("Response from /admin/create:", response);

      if (
        response.statusCode === 406 &&
        response.message &&
        response.message.includes("Account already exists")
      ) {
        setAddAdminErrorMsg("Admin with this email already exists.");
        toast.error("Admin with this email already exists.");
      } else if (response.statusCode === 200 || response.statusCode === 201) {
        toast.success(response.message || "Admin created successfully!", {
          autoClose: 1000,
          onClose: () => {
            setShowAddAdminModal(false); // Close the modal
            handleTabChange(activeTab); // Refresh the admin list
          },
        });
      } else {
        setAddAdminErrorMsg(
          response.message || "Failed to create admin. Please try again."
        );
        toast.error(response.message || "Failed to create admin. Try again.");
      }
    } catch (error) {
      console.error("API request failed (network/server error):", error);
      setAddAdminErrorMsg(
        "Error creating admin. Please check your network or server connection."
      );
      toast.error("Error creating admin. Please try again.");
    } finally {
      setIsAddingAdmin(false); // Stop loading animation for modal
    }
  };

  const fetchRoledata = async () => {
    const token = localStorage.getItem("token");
    try {
      const response = await getWithTokenAPI(token, "/rolemanagement/find-all");
      if (response.statusCode === 200) {
        const formattedRoles = response.data.roles.map((role) => ({
          role_id: role._id,
          role_name: role.role_name,
        }));

        setrolesdata(formattedRoles);
      } else {
        // showToastErrorMsg("Error fetching dashboard data:", response.message);
      }
    } catch (error) {
      console.error("API Error:", error);
      // showToastErrorMsg("API Error:", error);
    }
  };
  useEffect(() => {
    fetchRoledata();
  }, []);

  return (
    <div className="container-fluid py-3">
      <ToastContainer />
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mb: 3,
          p: 2,
          backgroundColor: lightGreen,
          borderRadius: 2,
          borderLeft: `5px solid ${primaryGreen}`,
        }}
      >
        <PermContactCalendarIcon
          sx={{ color: primaryGreen, fontSize: 45, mr: 2 }}
        />
        <Box>
          <Typography
            variant="h4"
            component="h1"
            sx={{ color: primaryGreen, fontWeight: "bold" }}
          >
            Resource
          </Typography>
          <Typography variant="body1" sx={{ color: "text.secondary" }}>
            Manage Resources
          </Typography>
        </Box>
      </Box>

      <div className="mb-3 row justify-content-between align-items-center">
        <div className="col-auto">
          <div
            style={{
              backgroundColor: lightGreen,
              padding: "5px", // Reduced padding from 10px to 5px
              borderRadius: "8px",
            }}
          >
            <ul className="nav nav-tabs" style={{ borderBottom: "none" }}>
              {[
                { id: "active", label: "Active" },
                { id: "inactive", label: "Inactive" },
              ].map(
                (
                  tab,
                  index // Added index to adjust margin for last button
                ) => (
                  <li key={tab.id} className="nav-item">
                    <button
                      className="nav-link"
                      style={{
                        backgroundColor:
                          activeTab === tab.id ? "#DC3545" : "white",
                        color: activeTab === tab.id ? "white" : "#DC3545",
                        border: "1px solid white",
                        // Adjusting margin based on index for spacing
                        marginRight: index === 0 ? "0px" : "0px", // Margin right only for the first button
                        marginLeft: index === 1 ? "7px" : "0px", // Margin left only for the second button
                        fontWeight: "bold",
                        borderRadius: "4px",
                        minWidth: "100px",
                      }}
                      onClick={() => handleTabChange(tab.id)}
                    >
                      {tab.label}
                    </button>
                  </li>
                )
              )}
            </ul>
          </div>
        </div>

        {permissions.includes("create_resource") && (
          <Button
            variant="contained"
            sx={{
              backgroundColor: primaryGreen,
              "&:hover": {
                backgroundColor: "white",
                border: "2px solid #DC3545",
                color: "#DC3545",
              },
              color: "white",
              fontWeight: "bold",
              textTransform: "none",
              width: { xs: "100%", md: "auto" }, // Full width on small, auto on large
              mt: { xs: 2, md: 0 }, // Margin top on small screens
            }}
            onClick={handleAddAdminClick}
          >
            <i className="bi bi-plus-circle me-2"></i>Add Admin
          </Button>
        )}
      </div>

      <div className="row">
        <div className="col-12">
          <div className="table-container border rounded">
            <div className="table-responsive">
              {loading ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "200px",
                  }}
                >
                  <CircularProgress style={{ color: "#DC3545" }} />
                  <Typography sx={{ ml: 2,  color: "#DC3545" }}>Loading admins...</Typography>
                </Box>
              ) : (
                <table className="table text-center m-0">
                  <thead
                    className="sticky-header fs-6"
                    style={{ backgroundColor: lightGreen}}
                  >
                    <tr className="role-thead">
                      <th className="table-header role-thead" style={{ textAlign: 'left' }}>S.No</th>
                      <th className="table-header role-thead" style={{ textAlign: 'left' }}>Name</th>
                      <th className="table-header role-thead"style={{ textAlign: 'left' }}>Phone No</th>
                      <th className="table-header role-thead"style={{ textAlign: 'left' }}>Email</th>
                      <th className="table-header role-thead"style={{ textAlign: 'left' }}>Status</th>
                      <th className="table-header role-thead"style={{ textAlign: 'left' }}>Action</th>
                    </tr>
                  </thead>
               <tbody>
  {filteredAdmins.length > 0 ? (
    filteredAdmins.map((admin, index) => (
      <tr key={admin._id || admin.id}>
        {/* Apply textAlign: 'left' to each td */}
        <td style={{ textAlign: "left" }}>{index + 1}</td>
        <td
          onClick={() => handleViewDetails(admin._id || admin.id)}
          style={{
            fontWeight: "bold",
            color: "#DC3545",
            cursor: "pointer",
            textAlign: "left", // Already here, but ensuring consistency
            textDecoration: "none",
          }}
          onMouseEnter={(e) => (e.target.style.textDecoration = "underline")}
          onMouseLeave={(e) => (e.target.style.textDecoration = "none")}
        >
          {admin.name}
        </td>
        <td style={{ textAlign: "left" }}>{admin.phone_no}</td>
        <td style={{ textAlign: "left" }}>{admin.email}</td>
        <td style={{ textAlign: "left" }}>
          <div className="form-check form-switch d-flex justify-content-center">
            {" "}
            {/* This div still centers the switch */}
            <input
              className="form-check-input"
              type="checkbox"
              role="switch"
              checked={admin.status === "Active"}
              onChange={() =>
                toggleAdminStatus(admin._id || admin.id, admin.status)
              }
              style={{
                width: "3em",
                height: "1.5em",
                backgroundColor:
                  admin.status === "Active" ? "#DC3545" : "transparent",
                border:
                  admin.status !== "Active" ? "2px solid #DC3545" : "none",
                boxShadow: "none",
              }}
            />
          </div>
        </td>
        <td style={{ textAlign: "left" }}>
          <i
            className="bi bi-eye-fill fs-5 cursor-pointer me-3"
            title="View Details"
            onClick={() => handleViewDetails(admin._id || admin.id)}
            style={{ color: "#DC3545" }}
          ></i>
          {permissions.includes("delete_resource") && (
            <i
              className="bi bi-trash text-danger fs-5 cursor-pointer"
              title="Delete"
              onClick={() => handleDeleteAdmin(admin._id || admin.id)}
            ></i>
          )}
        </td>
      </tr>
    ))
  ) : (
    <tr>
      <td colSpan="6" className="text-center">
        No {activeTab} admins found.
      </td>
    </tr>
  )}
</tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Add Admin Modal */}
      {showAddAdminModal && (
        <div
          className="modal fade show"
          tabIndex="-1"
          style={{ display: "block", backgroundColor: "rgba(0, 0, 0, 0.5)" }}
          aria-labelledby="addAdminModalLabel"
          aria-hidden="true"
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div
                className="modal-header text-white"
                style={{ backgroundColor: "#DC3545", fontWeight: "bold" }}
              >
                <h5 className="modal-title" id="addAdminModalLabel">
                  Add New Admin
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  aria-label="Close"
                  onClick={() => setShowAddAdminModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                {addAdminErrorMsg && (
                  <div className="alert alert-danger" role="alert">
                    {addAdminErrorMsg}
                  </div>
                )}
                <form onSubmit={handleAddAdminSubmit}>
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label htmlFor="newName" className="form-label">
                        Name:
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="newName"
                        name="name"
                        value={newAdmin.name}
                        onChange={handleNewAdminInputChange}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="newEmail" className="form-label">
                        Email:
                      </label>
                      <input
                        type="email"
                        className="form-control"
                        id="newEmail"
                        name="email"
                        value={newAdmin.email}
                        onChange={handleNewAdminInputChange}
                        required
                      />
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label htmlFor="newPassword" className="form-label">
                        Password:
                      </label>
                      <div className="input-group">
                        <input
                          type={showPassword ? "text" : "password"}
                          className="form-control"
                          id="newPassword"
                          name="password"
                          value={newAdmin.password}
                          onChange={handleNewAdminInputChange}
                          required
                        />
                        <button
                          className="btn btn-outline-secondary"
                          type="button"
                          onClick={togglePasswordVisibility}
                        >
                          <i
                            className={`bi ${
                              showPassword ? "bi-eye-slash" : "bi-eye"
                            }`}
                          ></i>
                        </button>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="newPhoneNo" className="form-label">
                        Phone No:
                      </label>
                      <input
                        type="tel"
                        className="form-control"
                        id="newPhoneNo"
                        name="phone_no"
                        value={newAdmin.phone_no}
                        onChange={handleNewAdminInputChange}
                        required
                      />
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-md-4">
                      <label htmlFor="newGender" className="form-label">
                        Gender:
                      </label>
                      <select
                        className="form-select"
                        id="newGender"
                        name="gender"
                        value={newAdmin.gender}
                        onChange={handleNewAdminInputChange}
                        required
                      >
                        <option value="">Select Gender</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    <div className="col-md-4">
                      <label htmlFor="newDateOfBirth" className="form-label">
                        Date of Birth:
                      </label>
                      <input
                        type="date"
                        className="form-control"
                        id="newDateOfBirth"
                        name="date_of_birth"
                        value={newAdmin.date_of_birth}
                        onChange={handleNewAdminInputChange}
                        required
                      />
                    </div>

                    <div className="mb-3 col-md-4">
                      <label className="m-2">Role :</label>
                      <select
                        name="role_id"
                        className="form-select"
                        value={newAdmin.role_id}
                        onChange={handleNewAdminInputChange}
                      >
                        <option value="">Select Role</option>
                        {rolesdata.map((rol) => (
                          <option key={rol.role_id} value={rol.role_id}>
                            {rol.role_name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="mb-3">
                    <label htmlFor="newAddress" className="form-label">
                      Address:
                    </label>
                    <textarea
                      className="form-control"
                      id="newAddress"
                      name="address"
                      rows="3"
                      value={newAdmin.address}
                      onChange={handleNewAdminInputChange}
                      required
                    ></textarea>
                  </div>

                  <div className="d-flex justify-content-end">
                    <button
                      type="button"
                      className="btn btn-secondary me-2"
                      onClick={() => setShowAddAdminModal(false)}
                      disabled={isAddingAdmin}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn text-white"
                      style={{ backgroundColor: "#DC3545", fontWeight: "bold" }}
                      disabled={isAddingAdmin}
                    >
                      {isAddingAdmin ? (
                        <CircularProgress size={24} color="inherit" />
                      ) : (
                        "Create Admin"
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminManagement;
